import { indentLess, insertTab } from "@codemirror/commands";
import { getOriginalDoc, unifiedMergeView } from "@codemirror/merge";
import { openSearchPanel } from "@codemirror/search";
import {
  EditorState as CMState,
  EditorSelection,
  Extension,
  StateEffect,
} from "@codemirror/state";
import { EditorView, keymap } from "@codemirror/view";
import classNames from "classnames";
import { basicSetup } from "codemirror";
import debounce from "lodash-es/debounce";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { asyncMemoizedSplitSQLWithDetails } from "utils/helpers/Query/splitSQLQuery";

import { AiSurfaceState, useAiAccessPolicy } from "hooks/useAiAccessPolicy";
import { isMac } from "services/browser";

import {
  Callbacks,
  aiAssistantStateField,
  aiAssistantWidgetExtension,
  focusAiAssistant,
} from "pages/DevelopWorkspace/Editor/Document/DocumentContent/DocumentEditor/extensions/aiAssistantWidget/aiAssistantWidget";
import {
  highlightStatementExtension,
  setHighlightEffect,
} from "pages/DevelopWorkspace/Editor/Document/DocumentContent/DocumentEditor/extensions/highlightStatement";
import {
  StatementGutterMarkerEffect,
  StatementGutterMarkerPendingEffect,
  StatementGutterMarkerState,
  findSliceAtPosition,
} from "pages/DevelopWorkspace/Editor/Document/DocumentContent/DocumentEditor/extensions/statementGutter/statementGutterMarker";
import { useDocuments } from "pages/DevelopWorkspace/contexts/DocumentsContext/hooks/useDocuments";
import { useSQLynx } from "pages/DevelopWorkspace/contexts/SQLynxContext/SQLynxLoader";
import { Script } from "pages/DevelopWorkspace/contexts/ScriptsContext/ScriptsContext";
import { getAiStateInitialSettings } from "pages/DevelopWorkspace/helpers/aiStateUtils";
import {
  DocumentAiState,
  EditorState,
} from "pages/DevelopWorkspace/workspace.types";

import { SearchIcon } from "components/Icons";

import { aiIconWidgetExtension } from "./extensions/aiIconWidget/aiIconWidget";
import { editorExtensions } from "./extensions/editorExtensions";
import { emptyLinePlaceholderExtension } from "./extensions/emptyLinePlaceholder";
import { mergePanel } from "./extensions/viewPlugins/mergePanel/mergePanel";
import {
  applyInitialAiState,
  configureAiAssistant,
  toggleSingleChunkClass,
} from "./utils/editorAiSetup";
import {
  createInitialCMState,
  serializeViewState,
  setFocusAndScrollToHead,
  syncSQLynxScript,
} from "./utils/editorViewSetup";
import { isTypingUpdate } from "./utils/isTypingUpdate";

import styles from "./DocumentEditor.module.scss";
import "./codemirror.scss";

interface DocumentEditorProps {
  documentId: string;
  documentScript: Script;
  onEditorStateUpdate: (editorState: EditorState) => void;
  onInit: (editor: EditorView) => void;
  onRun: (content: string, selectionOffset: number, lineNumber: number) => void;
}

const DocumentEditor = (props: DocumentEditorProps) => {
  const { onEditorStateUpdate, onRun, onInit, documentScript, documentId } =
    props;

  const {
    state,
    actions: {
      updateDocumentAIState,
      addDocumentAIState,
      removeDocumentAIState,
      handleAIOperation,
      cancelAIOperation,
    },
  } = useDocuments();

  const { sqlynxScript } = useSQLynx();
  const aiPolicy = useAiAccessPolicy();
  const { t } = useTranslation();

  const runHandler = useRef(onRun);
  const acceptAllHandler = useRef<() => void>(() => {});
  const rejectAllHandler = useRef<() => void>(() => {});
  const editorHostRef = useRef<HTMLDivElement | null>(null);
  const editorView = useRef<EditorView | null>(null);
  const debouncedOnChange = useRef(
    debounce((editorState: EditorState) => {
      onEditorStateUpdate(editorState);
    }, 150)
  );
  const handleCopilotButtonMouseLeaveRef = useRef<() => void>(() => {});

  const document = state.documents.find(doc => doc.id === documentId);

  const isMergeMode = documentScript.editorState.original !== null;

  const scheduleViewDispatch = (effects: StateEffect<unknown>[]) => {
    const view = editorView.current;
    if (!view || effects.length === 0) return;
    const run = () => {
      const current = editorView.current;
      if (!current) return;
      current.dispatch({ effects });
    };
    if (typeof queueMicrotask === "function") {
      queueMicrotask(run);
    } else if (typeof requestAnimationFrame === "function") {
      requestAnimationFrame(() => run());
    } else {
      setTimeout(run, 0);
    }
  };

  const aiWidgetCallbacks = useMemo((): Callbacks => {
    const acceptMergePreservingHistory = () => {
      const view = editorView.current;
      if (!view) return;
      if (documentScript.editorState.original == null) return;
      const nextSerialized: EditorState = serializeViewState(view, null);
      onEditorStateUpdate(nextSerialized);
      removeDocumentAIState(documentId);
    };

    const rejectMergePreservingHistory = () => {
      const original = documentScript.editorState.original;
      if (original == null) return;
      const view = editorView.current;
      if (!view) return;
      const current = view.state.selection.ranges[0];
      const clamp = (pos: number) =>
        Math.max(0, Math.min(pos, original.length));
      const nextSelection = EditorSelection.create([
        EditorSelection.range(clamp(current.anchor), clamp(current.head)),
      ]);

      view.dispatch({
        changes: { from: 0, to: view.state.doc.length, insert: original },
        selection: nextSelection,
        userEvent: "revert",
      });
      const nextSerialized: EditorState = serializeViewState(view, null);
      onEditorStateUpdate(nextSerialized);

      removeDocumentAIState(documentId);
    };

    const callbacks: Callbacks = {
      onUpdatePanel: (updates: Partial<DocumentAiState>) =>
        updateDocumentAIState(documentId, updates),
      onClose: () => {
        removeDocumentAIState(documentId);
        editorView.current?.focus();
      },
      onAction: (userPrompt: string) =>
        handleAIOperation(documentId, userPrompt),
      onStop: () => cancelAIOperation(documentId),
      onAcceptAll: acceptMergePreservingHistory,
      onRejectAll: rejectMergePreservingHistory,
    };
    return callbacks;
  }, [
    documentId,
    updateDocumentAIState,
    removeDocumentAIState,
    handleAIOperation,
    cancelAIOperation,
    onEditorStateUpdate,
    documentScript.editorState.original,
  ]);

  useEffect(() => {
    acceptAllHandler.current = () => aiWidgetCallbacks.onAcceptAll();
    rejectAllHandler.current = () => aiWidgetCallbacks.onRejectAll();
  }, [aiWidgetCallbacks]);

  useEffect(() => {
    if (!editorView.current) return;
    const effects = configureAiAssistant(editorView.current, {
      documentId,
      callbacks: aiWidgetCallbacks,
      aiState: document?.aiState ?? null,
    });
    scheduleViewDispatch(effects);
  }, [document?.aiState, aiWidgetCallbacks, documentId]);

  useEffect(() => {
    runHandler.current = onRun;
  }, [onRun]);

  useEffect(() => {
    // scroll to the ai assistant widget (Fix with AI case)
    if (document?.aiState?.autoRun) {
      editorView.current?.dispatch({
        effects: EditorView.scrollIntoView(document.aiState.widgetPosition, {
          y: "center",
          x: "start",
        }),
      });
    }
  }, [document?.aiState]);

  const destroyEditorView = () => {
    const debounceRef = debouncedOnChange.current;
    debounceRef?.flush();
    debounceRef?.cancel();

    if (editorView.current) {
      editorView.current.destroy();

      editorView.current = null;
    }
  };

  const handleCopilotButtonMouseEnter = () => {
    if (!editorView.current) return;
    const slice = editorView.current.state.field(
      StatementGutterMarkerState
    )?.slice;

    const range = editorView.current.state.selection.main;
    const hasSelection = !range.empty;

    if (hasSelection) {
      return;
    }

    if (slice) {
      editorView.current.dispatch({
        effects: setHighlightEffect.of({
          from: slice.pos[0],
          to: slice.pos[1],
        }),
      });
    }
  };

  const handleCopilotTrigger = () => {
    if (aiPolicy.editor !== AiSurfaceState.Enabled) return;
    if (!editorView.current) return;

    const internal = editorView.current.state.field(
      aiAssistantStateField,
      false
    );
    if (internal?.aiState) {
      const existingWidgetPosition = internal.aiState.widgetPosition;
      editorView.current?.dispatch({
        effects: EditorView.scrollIntoView(existingWidgetPosition, {
          y: "center",
          x: "start",
        }),
      });
      editorView.current?.dispatch({ effects: focusAiAssistant.of(null) });
      return;
    }

    const settings = getAiStateInitialSettings(editorView.current.state);

    addDocumentAIState(documentId, {
      ...settings,
      text: "",
      autoRun: false,
      externalInitializer: null,
    });

    editorView.current?.dispatch({
      effects: EditorView.scrollIntoView(settings.widgetPosition, {
        y: "center",
        x: "start",
      }),
    });
    editorView.current?.dispatch({ effects: focusAiAssistant.of(null) });
  };

  const renderEditorView = () => {
    destroyEditorView();

    const node = editorHostRef.current;
    if (!node || editorView.current) return;

    const refreshStatementGutterForCurrentSelection = (view: EditorView) => {
      const text = view.state.doc;
      const sql = text.toString();
      const cursor = view.state.selection.ranges[0].from;

      const currentMarkerState = view.state.field(StatementGutterMarkerState);
      if (!currentMarkerState?.pending) {
        view.dispatch({
          effects: StatementGutterMarkerPendingEffect.of(true),
        });
      }

      asyncMemoizedSplitSQLWithDetails(sql, slices => {
        if (!editorView.current) return;
        if (
          !editorView.current.state.doc.eq(text) ||
          cursor !== editorView.current.state.selection.ranges[0].from
        ) {
          return;
        }
        const slice = findSliceAtPosition(slices, cursor);
        editorView.current.dispatch({
          effects: StatementGutterMarkerEffect.of({
            slice,
            pending: false,
          }),
        });
      });
    };

    const extensions: Extension[] = [
      ...editorExtensions,
      EditorView.updateListener.of(update => {
        if (!editorView.current) return;

        const hasStatementEffects = update.transactions.some(tr =>
          tr.effects.some(
            e =>
              e.is(StatementGutterMarkerEffect) ||
              e.is(StatementGutterMarkerPendingEffect)
          )
        );

        if (
          (update.docChanged || update.selectionSet) &&
          !hasStatementEffects
        ) {
          refreshStatementGutterForCurrentSelection(editorView.current);
        }

        const originalDoc = isMergeMode
          ? getOriginalDoc(editorView.current.state)
          : null;
        const original = originalDoc ? originalDoc.toString() : null;

        const currentDoc = editorView.current.state.doc.toString();
        const allChunksResolved = original === currentDoc;

        if (update.docChanged || update.selectionSet) {
          if (editorView.current.state) {
            const nextState: EditorState = serializeViewState(
              editorView.current,
              allChunksResolved ? null : original
            );

            const isTyping = isTypingUpdate(update.transactions);

            if (isTyping) {
              debouncedOnChange.current(nextState);
            } else {
              onEditorStateUpdate(nextState);
            }
          }
        }

        if (allChunksResolved) {
          setTimeout(() => {
            removeDocumentAIState(documentId);
            // Ensure the editor regains focus after the merge completes via plugin buttons
            editorView.current?.focus();
          }, 0);
        }
      }),
      keymap.of([
        {
          key: "Mod-k",
          preventDefault: true,
          run: () => {
            handleCopilotTrigger();
            return true;
          },
        },
        {
          key: "Tab",
          preventDefault: true,
          run: insertTab,
        },
        {
          key: "Shift-Tab",
          preventDefault: true,
          run: indentLess,
        },
        {
          key: "Ctrl-g",
          mac: "Cmd-g",
          preventDefault: true,
          run: () => {
            return true;
          },
        },
        {
          key: "Ctrl-Alt-g",
          mac: "Cmd-Alt-g",
          preventDefault: true,
          run: () => {
            return true;
          },
        },
        {
          key: "Ctrl-Shift-Enter",
          mac: "Cmd-Shift-Enter",
          run: view => {
            asyncMemoizedSplitSQLWithDetails.flush();

            runHandler.current(view.state.doc.toString(), 0, 1);
            return true;
          },
        },
        {
          key: "Ctrl-Enter",
          mac: "Cmd-Enter",
          run: view => {
            asyncMemoizedSplitSQLWithDetails.flush();

            const range = view.state.selection.ranges[0];

            if (range.from === range.to) {
              const slice = view.state.field(StatementGutterMarkerState).slice;

              if (slice) {
                const lineNumber = view.state.doc.lineAt(slice.pos[0]).number;
                runHandler.current(slice.text, slice.pos[0], lineNumber);
                return true;
              }
              return false;
            }

            const selectedText = view.state.doc.sliceString(
              range.from,
              range.to
            );

            const lineNumber = view.state.doc.lineAt(range.from).number;
            runHandler.current(selectedText, range.from, lineNumber);
            return true;
          },
        },
      ]),
      basicSetup,
    ];

    if (aiPolicy.editor === AiSurfaceState.Enabled) {
      if (!document?.aiState) {
        const aiShortcut = `${isMac ? "⌘" : "Ctrl"} + K`;
        extensions.push(
          emptyLinePlaceholderExtension(
            t("editor.placeholder.generate_hotkey", { hotkey: aiShortcut })
          )
        );
      }
      extensions.push(
        ...aiAssistantWidgetExtension(documentId, aiWidgetCallbacks),
        aiIconWidgetExtension({
          onClick: () => handleCopilotTrigger(),
          onMouseEnter: () => handleCopilotButtonMouseEnter(),
          onMouseLeave: () => handleCopilotButtonMouseLeaveRef.current(),
        })
      );

      // Disable highlight when in merge mode
      if (!isMergeMode) {
        extensions.push(highlightStatementExtension);
      }
    }

    const original = documentScript.editorState.original;

    if (original !== null) {
      extensions.push(
        // Use a transaction filter so we also block completion acceptance and similar doc-changing actions.
        CMState.transactionFilter.of(tr => {
          if (!tr.docChanged) return tr;
          if (tr.isUserEvent("revert") || tr.isUserEvent("accept")) return tr;

          const internal = tr.state.field(aiAssistantStateField, false);
          const pos = internal?.pos;
          if (typeof pos === "number") {
            scheduleViewDispatch([
              EditorView.scrollIntoView(pos, { y: "center", x: "start" }),
            ]);

            if (editorHostRef.current) {
              editorHostRef.current
                ?.querySelector<HTMLElement>(
                  '[data-testid="ai-assistant-primary"]'
                )
                ?.focus();
              editorHostRef.current.classList.add("cm-ai-widget-warning");
              setTimeout(
                () =>
                  editorHostRef.current?.classList.remove(
                    "cm-ai-widget-warning"
                  ),
                1000
              );
            }
          }
          // Drop the transaction entirely
          return [];
        }),
        unifiedMergeView({ original, gutter: false, mergeControls: true }),
        // Merge actions panel rendered only when AI widget is out of viewport
        mergePanel(
          () => acceptAllHandler.current?.(),
          () => rejectAllHandler.current?.()
        )
      );
    }

    const initialState = createInitialCMState(
      documentScript.editorState,
      extensions
    );
    editorView.current = new EditorView({ state: initialState, parent: node });

    // Initialize statement gutter for the current selection immediately on mount
    if (editorView.current) {
      refreshStatementGutterForCurrentSelection(editorView.current);
    }

    if (sqlynxScript) {
      syncSQLynxScript(
        editorView.current,
        documentScript.editorState,
        sqlynxScript
      );
    }

    if (original !== null && document?.aiState) {
      toggleSingleChunkClass(editorView.current, node);
    }

    // If AI prompt is already open (aiState provided externally), ensure widget is mounted immediately
    if (document?.aiState) {
      // Ensure AI facet is configured immediately on mount
      const cfgEffects = configureAiAssistant(editorView.current, {
        documentId,
        callbacks: aiWidgetCallbacks,
        aiState: document.aiState,
      });
      editorView.current.dispatch({ effects: cfgEffects });
      applyInitialAiState(editorView.current, document.aiState);
    }

    const head = documentScript.editorState?.selection?.ranges?.[0]?.head || 0;
    setFocusAndScrollToHead(editorView.current, head);

    onInit(editorView.current);
  };

  useEffect(() => {
    renderEditorView();

    return () => {
      destroyEditorView();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run only on mount and unmount
  }, []);

  const handleCopilotButtonMouseLeave = useCallback(() => {
    if (!editorView.current) {
      return;
    }

    if (document?.aiState) {
      return;
    }

    editorView.current.dispatch({
      effects: setHighlightEffect.of(null),
    });
  }, [document?.aiState]);

  useEffect(() => {
    handleCopilotButtonMouseLeaveRef.current = handleCopilotButtonMouseLeave;
  }, [handleCopilotButtonMouseLeave]);

  if (!document) {
    return null;
  }

  return (
    <div
      className={classNames(styles.editorContainer, {
        "cm-with-ai-state":
          aiPolicy.editor === AiSurfaceState.Enabled && !!document?.aiState,
      })}
    >
      <div className={styles.controls}>
        <div
          className={styles.searchIcon}
          onClick={() => {
            if (editorView.current) openSearchPanel(editorView.current);
          }}
        >
          <SearchIcon />
        </div>
      </div>
      <div
        style={{ width: "100%", height: "100%" }}
        ref={editorHostRef}
      />
    </div>
  );
};

export default DocumentEditor;
